import { InfoCircleOutlined } from '@ant-design/icons';
import { Divider, Input } from 'antd';
import { Select } from 'antd5';
import i18n from 'i18next';
import { get } from 'lodash';
import moment, { type Moment } from 'moment';
import { Link } from 'react-router-dom';

import { VendorOrgChooser } from 'components/choosers/VendorOrgChooser';
import {
  AssetFilter,
  DefaultStateToGraphqlKeyMap,
  SiteFilter,
} from 'components/DataTable/customFilters';
import { Tooltip } from 'components/ui';
import { UTCDatePicker } from 'components/UTCDatePicker';
import {
  getAssetSite,
  getUniqueIdentifier,
  renderAsset,
  renderAssetSite,
} from 'horizon/components/Assets/AssetDetails/helpers';
import { receivedInspectionDeliveriesLatestCreated } from 'horizon/components/InspectionsList/utils';
import { InspectionTypeChooser } from 'horizon/routes/Inspections/CreateSingleInspection/InspectionTypeChooser';
import { InspectionVendorChooser } from 'horizon/routes/Inspections/CreateSingleInspection/InspectionVendorChooser';
import { type PaginatedInspection } from 'horizon/routes/Inspections/types';
import { getInspectionVendorDisplayName } from 'horizon/routes/Inspections/vendorUtils';
import { type TEditableTableColumnDef, type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAsset,
  type AtlasGqlAssetType,
  type AtlasGqlInspection,
  type AtlasGqlInspectionType,
  AtlasGqlFilterOperator,
  AtlasGqlInspectionStatus,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { StatusTag } from 'utils/color/helpers';
import { formatDate, startCase } from 'utils/format';
import { INSPECTION_VENDOR_NAMES } from 'utils/release-toggles';

import { getFilter, getFilterDropdown, getOnFilter, None, sortBy } from './helpers';

export const removeInspectionLink = (column: TTableColumnDef<PaginatedInspection>) => {
  return {
    ...column,
    render: (_: string | undefined, record: PaginatedInspection) => {
      const { inspectionDate } = record || {};
      return inspectionDate ? (
        inspectionDate
      ) : (
        <None message={i18n.t('inspection.noDate', 'No Date')} />
      );
    },
  };
};

const dateFilterConfig = { key: 'inspectionDate', type: 'date' };

export const date: TTableColumnDef<PaginatedInspection> = {
  title: i18n.t('table.inspected', 'Inspected'),
  key: 'inspectionDate',
  dataIndex: 'inspectionDate',
  sortDirections: ['descend', 'ascend'],
  sorter: sortBy('inspectionDate'),
  filterDropdown: getFilterDropdown(dateFilterConfig),
  onFilter: getOnFilter(dateFilterConfig as any),
  get: record => get(record, 'inspectionDate'),
  filterProperty: 'inspectionDate',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-date-header',
    }) as any,
  render(_, record) {
    const { id, inspectionDate, type } = record || {};
    return (
      <span data-testid="inspection-date">
        {[id, inspectionDate].every(Boolean) ? (
          <Link
            to={`/${
              type?.name === 'Internal Inspection' ? 'internal-inspections' : 'inspections'
            }/${id}`}
          >
            {inspectionDate}
          </Link>
        ) : (
          <None message={i18n.t('inspection.noDate', 'No Date')} />
        )}
      </span>
    );
  },
};

export const dateTurbineLink: any = {
  ...date,
  render(_: any, record: any) {
    const { id, inspectionDate, asset } = record || {};
    return (
      <span data-testid="inspection-turbine-date">
        {[id, inspectionDate].every(Boolean) ? (
          <Link to={`/assets/${asset?.id}/inspections/${id}`}>{formatDate(inspectionDate)}</Link>
        ) : (
          <None message={i18n.t('inspection.noDate', 'No Date')} />
        )}
      </span>
    );
  },
  edit(record: any, { onChange, ...props }: any) {
    const { inspectionDate: value } = record || {};
    return (
      <UTCDatePicker
        defaultValue={value && moment(value)}
        onChange={(date: Moment | null) => {
          onChange?.({ inspectionDate: date });
        }}
        {...props}
      />
    );
  },
};

const createdDateFilterConfig = { key: 'created', type: 'date' };

export const createdDate: TTableColumnDef<AtlasGqlInspection> = {
  title: i18n.t('table.created', 'Created'),
  key: 'created',
  dataIndex: 'created',
  sortDirections: ['descend', 'ascend'],
  sorter: sortBy('created'),
  filterDropdown: getFilterDropdown(createdDateFilterConfig),
  onFilter: getOnFilter(createdDateFilterConfig as any),
  get: (record: AtlasGqlInspection) => get(record, 'created'),
  filterProperty: 'created',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-created-date-header',
    }) as any,
  render(_, record: AtlasGqlInspection) {
    const { created } = record || {};
    return <span data-testid="inspection-created-date">{formatDate(created)}</span>;
  },
};

const lastDeliveredFilterConfig = { key: 'lastDelivered', type: 'date' };

export const lastDelivered: TTableColumnDef<AtlasGqlInspection> = {
  title: (
    <span data-testid="inspection-last-delivered-header-label">
      {i18n.t('inspection.lastDelivered', 'Last Delivered')}{' '}
      <Tooltip
        children={<InfoCircleOutlined data-testid="inspection-last-delivered-info" />}
        title={
          <span data-testid="inspection-last-delivered-info-tooltip">
            {i18n.t(
              'inspection.lastDeliveredTooltipPart1',
              'The date this Inspection was most recently delivered'
            )}{' '}
            <strong>{i18n.t('inspection.lastDeliveredTooltipTo', 'to')}</strong>{' '}
            {i18n.t(
              'inspection.lastDeliveredTooltipPart2',
              'this Organization. No date indicates that the Inspection was created directly in this Organization, not delivered from another Vendor.'
            )}
          </span>
        }
      />
    </span>
  ),
  key: 'lastDelivered',
  dataIndex: 'receivedDeliveries',
  sortDirections: ['descend', 'ascend'],
  sorter: sortBy('lastDelivered'),
  sortKey: 'lastDelivered',
  get: (record: AtlasGqlInspection) => {
    const deliveries = record?.receivedDeliveries;
    if (deliveries?.length) {
      const latestCreated = receivedInspectionDeliveriesLatestCreated(deliveries);
      return latestCreated ? new Date(latestCreated).getTime() : 0;
    }
    return 0;
  },
  filterDropdown: getFilterDropdown(lastDeliveredFilterConfig),
  onFilter: getOnFilter(lastDeliveredFilterConfig as any),
  filterProperty: 'lastDelivered',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-last-delivered-header',
    }) as any,
  render(_, record: AtlasGqlInspection) {
    const deliveries = record?.receivedDeliveries;
    if (deliveries?.length) {
      const latestCreated = receivedInspectionDeliveriesLatestCreated(deliveries);
      if (latestCreated) {
        return <span data-testid="inspection-last-delivered">{formatDate(latestCreated)}</span>;
      }
    }
    return (
      <span data-testid="inspection-last-delivered">
        <None />
      </span>
    );
  },
};

export const createdBy: TTableColumnDef<AtlasGqlInspection> = {
  title: i18n.t('table.createdBy', 'Created By'),
  key: 'createdBy',
  dataIndex: 'createdBy',
  sorter: sortBy('createdBy.email'),
  get: (record: AtlasGqlInspection) => get(record, ['createdBy', 'email']),
  filterProperty: 'createdBy.email',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-created-by-header',
    }) as any,
  render(_, record: AtlasGqlInspection) {
    const { createdBy } = record || {};
    if (!createdBy?.email) {
      return (
        <span data-testid="inspection-createdBy-email-none">
          <None />
        </span>
      );
    }
    return <span data-testid="inspection-createdBy-email">{createdBy?.email}</span>;
  },
};

const statuses = [
  'ANALYSIS_PENDING',
  'ANALYSIS_IN_PROGRESS',
  'ANALYSIS_COMPLETE',
  'DRAFT',
  'DATA_CAPTURE_PENDING',
  'DATA_CAPTURE_IN_PROGRESS',
  'DELIVERED',
  'RELEASED',
];

const statusFilterConfig = {
  key: 'status',
  type: 'string',
  values: statuses,
};

export const status: TEditableTableColumnDef<PaginatedInspection> = {
  key: 'status',
  title: i18n.t('table.status', 'Status'),
  dataIndex: 'status',
  editRule: 'INSPECTIONS_EDIT',
  sorter: sortBy('status'),
  get: (record: PaginatedInspection) => get(record, 'status'),
  filters: statuses.map(s => {
    return { text: startCase(s), value: s };
  }),
  filterProperty: 'status',
  onFilter: getOnFilter(statusFilterConfig as any),
  filterDropdown: getFilterDropdown(statusFilterConfig),
  render(_: string | undefined, record: PaginatedInspection) {
    const status = record?.status;

    if (!status) {
      return <None />;
    }
    return <StatusTag data-testid="inspection-status" status={status} />;
  },
  edit(record: any, { onChange }: { onChange: (data: any) => void }) {
    return (
      <Select
        defaultValue={record?.status}
        onChange={onChange && (status => onChange({ status }))}
        options={[
          {
            value: AtlasGqlInspectionStatus.DataCaptureInProgress,
            label: 'Data Capture In Progress',
          },
          { value: AtlasGqlInspectionStatus.AnalysisPending, label: 'Analysis Pending' },
          { value: AtlasGqlInspectionStatus.AnalysisInProgress, label: 'Analysis In Progress' },
          { value: AtlasGqlInspectionStatus.AnalysisComplete, label: 'Analysis Complete' },
        ]}
      />
    );
  },
};

export const getInspectionTypeColumn = (
  inspectionTypes: AtlasGqlInspectionType[]
): TEditableTableColumnDef<PaginatedInspection> => ({
  title: i18n.t('table.inspectionType', 'Inspection Type'),
  sorter: sortBy('type.name'),
  get: (record: PaginatedInspection) => get(record, ['type', 'name']),
  key: 'type',
  dataIndex: 'type',
  filters: inspectionTypes.map(({ id, name }) => {
    return { text: name, value: id };
  }),
  filterProperty: 'type.name',
  filterDropdown: getFilterDropdown({
    key: 'type.name',
    type: 'string',
  }),
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-type-header',
    }) as any,
  render(type: any) {
    return <span data-testid="inspection-type">{type?.name || <None />}</span>;
  },
  edit(record: PaginatedInspection, { onChange }: { onChange: (data: any) => void }) {
    const { type: inspectionType, asset, vendor } = record || {};
    return (
      <InspectionTypeChooser
        disabled={false}
        assetTypeId={asset?.assetType?.id}
        vendorId={vendor?.id}
        placeholder={inspectionType?.name ?? ''}
        // only include external types
        onlyExternal={true}
        onChange={onChange && (inspectionType => onChange({ typeId: inspectionType }))}
      />
    );
  },
});

export const type: TEditableTableColumnDef<PaginatedInspection> = {
  title: i18n.t('table.inspectionType', 'Inspection Type'),
  sorter: sortBy('type.name'),
  get: (record: PaginatedInspection) => get(record, ['type', 'name']),
  key: 'type',
  dataIndex: 'type',
  filterProperty: 'type.name',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-type-header',
    }) as any,
  render(type: any) {
    return <span data-testid="inspection-type">{type?.name || <None />}</span>;
  },
  edit(record: PaginatedInspection, { onChange }: { onChange: (data: any) => void }) {
    const { type: inspectionType, asset, vendor } = record || {};
    return (
      <InspectionTypeChooser
        disabled={false}
        assetTypeId={asset?.assetType?.id}
        vendorId={vendor?.id}
        placeholder={inspectionType?.name ?? ''}
        // only include external types
        onlyExternal={true}
        onChange={onChange && (inspectionType => onChange({ typeId: inspectionType }))}
      />
    );
  },
};

const locationSorters = ['location.name', 'turbine.name'].map(sortBy);

export const location = {
  title: i18n.t('table.site', 'Site'),
  key: 'location',
  dataIndex: 'location',
  defaultFilterOperator: 'CONTAINS',
  filterProperty: ['location.name', 'turbine.name'] as any,
  sorter(...args: any) {
    return locationSorters.map(fn => fn(...args)).find(Boolean);
  },
  get: (record: PaginatedInspection) => get(record, ['location', 'name']),
  render(_: any, { turbine, location }: any = {}) {
    if (!turbine && !location) {
      return (
        <span data-testid="inspection-site">
          <None />
        </span>
      );
    }

    return (
      <span data-testid="inspection-site">
        {location && (
          <>
            <Link to={`/sites/${location.id}`}>{location.name}</Link>
            <Divider type="vertical" />
          </>
        )}
        {turbine && (
          <Link to={`/turbines/${turbine.id}`}>
            {i18n.t('table.turbine', 'Turbine')} {turbine.name}
          </Link>
        )}
      </span>
    );
  },
};

export const site: TTableColumnDef<PaginatedInspection> = {
  title: i18n.t('table.site', 'Site'),
  key: 'site',
  dataIndex: 'site',
  defaultFilterOperator: AtlasGqlFilterOperator.Equals,
  filterProperty: 'site',
  filterDropdown: props => <SiteFilter {...props} />,
  sortKey: 'site',
  sorter: sortBy('site'),
  get: (record: PaginatedInspection) => {
    const asset = record?.asset;
    if (!asset) {
      return '';
    }
    return getAssetSite(asset)?.identifier || '';
  },
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-site-header',
    }) as any,
  render(_, { asset }: PaginatedInspection) {
    return <span data-testid="inspection-site">{renderAssetSite(asset)}</span>;
  },
};

// Replaced by `getAssetColumn` in the table, but used for detail page header
export const assetWithoutFilters: TTableColumnDef<PaginatedInspection> = {
  title: i18n.t('table.asset', 'Asset'),
  key: 'assetId',
  dataIndex: 'asset',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-asset-header',
    }) as any,
  render(_, { asset }: PaginatedInspection) {
    return <span data-testid="inspection-asset">{asset ? renderAsset(asset) : <None />}</span>;
  },
};

// "assetId" cannot be used since the service already supports filtering by "assetId" through different logic - renaming here to avoid breaking legacy API usages
const InspectionsAssetFilterStateToGraphqlKeyMap = {
  ...DefaultStateToGraphqlKeyMap,
  assets: 'assetOrComponentId',
};
export const getAssetColumn = (
  tableId: string,
  lockedAssetTypes?: AtlasGqlAssetType[],
  lockedAssetIds?: string[]
): TTableColumnDef<PaginatedInspection> => ({
  key: 'asset',
  allKeys: Object.values(InspectionsAssetFilterStateToGraphqlKeyMap),
  title: i18n.t('table.asset', 'Asset'),
  dataIndex: 'asset',
  filterProperty: 'asset',
  filtered: !!lockedAssetIds?.length || !!lockedAssetTypes?.length || undefined,
  filterDropdown: props => (
    <AssetFilter
      {...props}
      tableId={tableId}
      lockedAssetTypes={lockedAssetTypes}
      lockedAssetIds={lockedAssetIds}
      stateToGraphqlKeyMap={InspectionsAssetFilterStateToGraphqlKeyMap}
    />
  ),
  serverSideFilterParsers: {
    assetOrComponentId: (v: string) => v.split(':')[0],
    componentId: (v: string) => v.split(':')[0],
  },
  sortKey: 'asset',
  sorter: sortBy('asset'),
  get: (record: PaginatedInspection) => {
    const asset = record?.asset as AtlasGqlAsset | undefined;
    return getUniqueIdentifier(asset) || '';
  },
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-asset-header',
    }) as any,
  render(_, { asset }: PaginatedInspection) {
    return <span data-testid="inspection-asset">{renderAsset(asset)}</span>;
  },
});

export const assetWithSite: TTableColumnDef<PaginatedInspection> = {
  title: i18n.t('table.asset', 'Asset'),
  key: 'assetId',
  dataIndex: 'asset',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-asset-header',
    }) as any,
  render(_, { asset }: PaginatedInspection) {
    return <span data-testid="inspection-asset">{renderAsset(asset, { includeSite: true })}</span>;
  },
};

// Data and filter now part of asset column in the table, but used for detail page header
export const assetType: TTableColumnDef<PaginatedInspection> = {
  title: i18n.t('table.assetType', 'Asset Type'),
  key: 'assetTypeName',
  dataIndex: 'assetType',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-asset-type-header',
    }) as any,
  render(_, { asset }: PaginatedInspection) {
    return (
      <span data-testid="inspection-asset-type">
        {asset?.assetType ? asset.assetType.name : <None />}
      </span>
    );
  },
};

export const description = {
  title: i18n.t('table.description', 'Description'),
  key: 'description',
  filterProperty: 'description',
  sorter: sortBy('description'),
  get: (record: PaginatedInspection) => get(record, 'description'),
  defaultFilterOperator: 'CONTAINS',
  render(record: PaginatedInspection) {
    const { description } = record || {};
    return <span data-testid="inspection-description">{description || <None />}</span>;
  },
  edit(record: PaginatedInspection, { onChange, ...props }: any) {
    const { description } = record || {};
    return (
      <Input
        style={{ maxWidth: '300px' }}
        defaultValue={description}
        onChange={onChange && (e => onChange({ description: e.target.value }))}
        {...props}
      />
    );
  },
};

export const task = {
  title: i18n.t('table.task', 'Task'),
  key: 'task',
  dataIndex: 'task',
  filterProperty: 'task.number',
  get: (record: PaginatedInspection) => get(record, ['task', 'number']),
  render: (task: any) => {
    const { id, number } = task || {};
    return [id, number].every(Boolean) ? <Link to={`/tasks/${id}`}>{number}</Link> : <None />;
  },
};

export const getVendorColumn = (
  vendors: Array<{ id: string; name: string }>
): TEditableTableColumnDef<PaginatedInspection> => {
  const vendorFilterConfig = {
    key: 'vendor.name',
    type: 'string',
    values: vendors.map(v => ({
      id: v.name,
      label: v.name,
    })),
  };

  return {
    title: i18n.t('table.vendor', 'Vendor'),
    // TODO: still sorting/filtering on the legacy name until Atlas supports `vendorOrganization`.
    sorter: sortBy('vendor.name'),
    get: (record: PaginatedInspection) => get(record, ['vendor', 'name']),
    key: 'vendor',
    dataIndex: 'vendor',
    filters: getFilter(vendorFilterConfig),
    filterProperty: 'vendor.name',
    onFilter: getOnFilter(vendorFilterConfig as any),
    filterDropdown: getFilterDropdown(vendorFilterConfig),
    onHeaderCell: () =>
      ({
        'data-testid': 'inspection-vendor-header',
      }) as any,
    render(_vendor: any, record: PaginatedInspection) {
      return <VendorDisplay record={record} />;
    },
    // No inline editor: the paginated Inspections list is read-only (columns are never wrapped in
    // `inlineEditable`). Vendor is edited from the Inspection Detail page via the `vendor` column.
  };
};

/**
 * Displays an Inspection's vendor - derived from org with inspection_vendor_names toggle
 * Fall back to legacy vendor
 */
const VendorDisplay = ({ record }: { record: PaginatedInspection }) => {
  const { hasReleaseToggle } = useAccountContext();

  const vendorName = getInspectionVendorDisplayName(
    record,
    hasReleaseToggle(INSPECTION_VENDOR_NAMES)
  );

  return <span data-testid="inspection-vendor">{vendorName || <None />}</span>;
};

/**
 * Chooses the vendor for an Inspection - vendor is org with inspection_vendor_names toggle
 * Fall back to legacy vendor
 */
const VendorEditor = ({
  record,
  onChange,
}: {
  record: PaginatedInspection;
  onChange: (data: any) => void;
}) => {
  const { hasReleaseToggle } = useAccountContext();
  const { type: inspectionType } = record || {};

  if (hasReleaseToggle(INSPECTION_VENDOR_NAMES)) {
    return (
      <VendorOrgChooser
        relationshipType="delivery"
        includeCurrentOrg={true}
        defaultValue={record?.vendorOrganization?.id}
        // An Inspection always has a vendor - it is required at creation - so there is no
        // permitted "no vendor" state to clear back to.
        allowClear={false}
        onChange={
          onChange &&
          // Mirrors the Work Order vendor editor: `null` rather than `undefined`, since Apollo
          // omits `undefined` input values and the change would not persist.
          (vendorOrg => onChange({ vendorOrganizationId: vendorOrg ?? null }))
        }
      />
    );
  }

  return (
    <InspectionVendorChooser
      disabled={false}
      typeId={inspectionType?.id}
      onChange={onChange && (vendor => onChange({ vendorId: vendor }))}
    />
  );
};

export const vendor: TEditableTableColumnDef<PaginatedInspection> = {
  title: i18n.t('table.vendor', 'Vendor'),
  // TODO - Update and filter when backend support of org name sorting/filtering is implemented.
  sorter: sortBy('vendor.name'),
  get: (record: PaginatedInspection) => get(record, ['vendor', 'name']),
  key: 'vendor',
  dataIndex: 'vendor',
  filterProperty: 'vendor.name',
  onHeaderCell: () =>
    ({
      'data-testid': 'inspection-vendor-header',
    }) as any,
  render(_vendor: any, record: PaginatedInspection) {
    return <VendorDisplay record={record} />;
  },
  edit(record: PaginatedInspection, { onChange }: { onChange: (data: any) => void }) {
    return <VendorEditor record={record} onChange={onChange} />;
  },
};

export const reviewed = {
  title: i18n.t('table.reviewedImages', 'Reviewed Images'),
  key: 'reviewedTotals',
  dataIndex: 'reviewedTotals',
  render(totals: any) {
    const { reviewed, total } = totals || {};
    return (
      <span data-testid="inspection-reviewed">{total ? `${reviewed}/${total}` : <None />}</span>
    );
  },
};

const inspectionColumns = () => [date, location];

export default inspectionColumns;
